/**
 * Storage Service - Fetch data from Firebase Storage
 * 
 * Handles loading large extraction data from GCS paths that exceed
 * Firestore's 1MB document size limit.
 */

import { getStorage, ref, getDownloadURL } from 'firebase/storage'
import app from '../lib/firebase'

const storage = getStorage(app)

/**
 * Convert GCS path or Firebase Storage path to download URL
 * @param path - GCS path like "gs://bucket/path/to/file.json" or Firebase Storage path like "users/uid/cases/caseId/documents/file.pdf"
 * @returns Download URL with authentication token
 */
export async function convertGCSPathToURL(path: string): Promise<string> {
  try {
    console.log('Converting storage path to download URL:', path)
    
    if (!path) {
      throw new Error('Invalid storage path: path is required')
    }
    
    let filePath: string
    
    if (path.startsWith('gs://')) {
      // Parse GCS path: gs://bucket/path/to/file.json
      const pathWithoutGS = path.replace('gs://', '')
      const firstSlashIndex = pathWithoutGS.indexOf('/')
      
      if (firstSlashIndex === -1) {
        throw new Error('Invalid GCS path format')
      }
      
      // Extract path after bucket name
      filePath = pathWithoutGS.substring(firstSlashIndex + 1)
    } else {
      // Treat as Firebase Storage path (no gs:// prefix)
      filePath = path
    }
    
    // Create Storage reference
    const storageRef = ref(storage, filePath)
    
    // Get download URL with auth token
    const downloadURL = await getDownloadURL(storageRef)
    
    console.log('✅ Storage path converted:', downloadURL.substring(0, 100) + '...')
    
    return downloadURL
  } catch (error) {
    console.error('Failed to convert storage path to URL:', error)
    throw new Error(`Failed to get download URL: ${error instanceof Error ? error.message : 'Unknown error'}`)
  }
}

/**
 * Load JSON data from Firebase Storage using GCS path
 * @param gcsPath - GCS path like "gs://bucket/path/to/extraction_result.json"
 * @returns Parsed JSON data
 */
export async function loadJSONFromStorage(gcsPath: string): Promise<any> {
  try {
    console.log('📥 Loading JSON from storage:', gcsPath)
    
    // Get download URL
    const downloadURL = await convertGCSPathToURL(gcsPath)
    
    // Fetch the JSON data
    const response = await fetch(downloadURL)
    
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`)
    }
    
    const data = await response.json()
    
    console.log('✅ Loaded JSON data:', {
      path: gcsPath,
      sizeBytes: JSON.stringify(data).length,
      topLevelKeys: Object.keys(data)
    })
    
    return data
  } catch (error) {
    console.error('Failed to load JSON from storage:', error)
    throw new Error(`Failed to load data: ${error instanceof Error ? error.message : 'Unknown error'}`)
  }
}

/**
 * Load full extraction data from GCS path
 * @param extractionPath - GCS path to extraction result
 * @returns Full extraction data with all transactions
 */
export async function loadFullExtractionData(extractionPath: string): Promise<{
  result: any[]
  citations?: any[]
}> {
  try {
    console.group('📦 Loading Full Extraction Data')
    console.log('Path:', extractionPath)
    
    const data = await loadJSONFromStorage(extractionPath)
    
    // Validate structure
    if (!data || typeof data !== 'object') {
      throw new Error('Invalid extraction data structure')
    }
    
    // Extract result and citations
    const result = data.result || []
    const citations = data.citations || []
    
    console.log('✅ Extraction data loaded:')
    console.log('  - Result items:', Array.isArray(result) ? result.length : 'Not an array')
    console.log('  - Has citations:', !!citations)
    
    if (Array.isArray(result) && result.length > 0) {
      const firstItem = result[0]
      if (firstItem && typeof firstItem === 'object') {
        console.log('  - First item keys:', Object.keys(firstItem))
        if (firstItem.transactions) {
          console.log('  - Transactions count:', firstItem.transactions.length)
        }
      }
    }
    
    console.groupEnd()
    
    return { result, citations }
  } catch (error) {
    console.error('Failed to load full extraction data:', error)
    throw error
  }
}

/**
 * Load validation result from GCS path
 * @param validationPath - GCS path to validation result
 * @returns Validation result data
 */
export async function loadValidationData(validationPath: string): Promise<any> {
  try {
    console.log('📥 Loading validation data from:', validationPath)
    
    const data = await loadJSONFromStorage(validationPath)
    
    console.log('✅ Validation data loaded:', {
      isValid: data.is_valid,
      hasStatistics: !!data.statistics
    })
    
    return data
  } catch (error) {
    console.error('Failed to load validation data:', error)
    throw error
  }
}

/**
 * Check if a GCS path is valid and accessible
 * @param gcsPath - GCS path to check
 * @returns True if accessible, false otherwise
 */
export async function isGCSPathAccessible(gcsPath: string): Promise<boolean> {
  try {
    await convertGCSPathToURL(gcsPath)
    return true
  } catch {
    return false
  }
}

/**
 * Get file size estimate from JSON data
 * @param data - JSON data object
 * @returns Size in bytes
 */
export function getJSONSizeEstimate(data: any): number {
  try {
    return new Blob([JSON.stringify(data)]).size
  } catch {
    return 0
  }
}

/**
 * Format bytes to human-readable string
 * @param bytes - Size in bytes
 * @returns Formatted string like "1.5 MB"
 */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 Bytes'
  
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i]
}

